// source https://github.com/apollographql/apollo-client/blob/master/src/react/hooks/utils/useDeepMemo.ts

import { useRef } from 'react';
import { isEqual } from 'lodash';

import { ensureDefined } from '@e-socna/utils';

/**
 * Memoize a result using deep equality. This hook has two advantages over
 * React.useMemo: it uses deep equality to compare memo keys, and it guarantees
 * that the memo function will only be called if the keys are unequal.
 * React.useMemo cannot be relied on to do this, since it is only a performance
 * optimization (see https://reactjs.org/docs/hooks-reference.html#usememo).
 */
export function useDeepMemo<TKey, TValue>(
  memoFn: () => TValue,
  key: TKey,
): TValue | undefined {
  const ref = useRef<{ key: TKey; value: TValue } | undefined>(undefined);

  const updateRef = () => {
    if (ref.current && isEqual(key, ref.current.key)) {
      return;
    }

    ref.current = { key, value: memoFn() };
  };

  // eslint-disable-next-line react-hooks/refs
  updateRef();

  // eslint-disable-next-line react-hooks/refs
  const { value } = ensureDefined<{ value: TValue }>({
    // eslint-disable-next-line react-hooks/refs
    value: ref.current?.value,
  });

  return value;
}
